import { Alert, FormHelperText, Link, Step, StepLabel, Stepper } from "@mui/material";
import { Fragment, useContext, useMemo } from 'react';
import { Grid, Layout, Loader, Paper, Typography } from "../../../components";
import { CreateAccountModal } from "../create-account-modal";
import StepNavigation from "../form-step-navigation";
import { useCreateAccountData } from "../hooks/useCreateAccountData";
import { useNewBidData } from "../hooks/useNewBidData";
import { NewBidFormStepOne } from "../new-bid-form-step-one";
import { NewBidFormStepThree } from "../new-bid-form-step-three";
import { NewBidFormStepTwo } from "../new-bid-form-step-two";
import { Ranking } from "./ranking";
import AuthContext from "../../../context/auth";
import { APP_CONTACTS } from "@next-pontos/settings";

export const NewBid = () => {
  const {me} = useContext(AuthContext)
  const {
    accountOptions,
    accounts,
    activeStep,
    bid,
    createAccountModalVisibilityControls,
    createAccountModalVisibility,
    dashboard,
    formik,
    programPriceSettings,
    programs,
    setActiveStep,
    refetchAccounts,
    ranking
  } = useNewBidData()

  const rankingMinPricePerK = useMemo(() => {
    const rows = (ranking?.ranking ?? [])
      .map((r: { price_per_k: number | string }) => Number(r.price_per_k))
      .filter((v: number) => Number.isFinite(v) && v > 0)

    if (rows.length === 0) return null

    return Math.min(...rows)
  }, [ranking])

  const isPriceAboveRanking = useMemo(() => {
    const current = Number(formik.values.price)
    if (!Number.isFinite(current) || current <= 0) return false
    if (!rankingMinPricePerK) return false
    return current > rankingMinPricePerK
  }, [formik.values.price, rankingMinPricePerK])

  const {
    account, 
    createAccountFormik, 
  } = useCreateAccountData({ formik, programs: programs.data, createAccountModalVisibilityControls, refetchAccounts });

  const formSteps = ["Programa e Conta", "Quantidade de Milhas e Valor", "Revisão"];

  const renderStepForm = (step: number) => {
    const stepProps = { formik, programPriceSettings, programs: programs.data };
  
    switch (step) {
      case 1:
        return <NewBidFormStepOne {...stepProps} accountOptions={accountOptions} createAccountModalVisibilityControls={createAccountModalVisibilityControls} />;
      case 2:
        return <NewBidFormStepTwo {...stepProps} dashboardData={dashboard.data} createAccountFormik={createAccountFormik}/>;
      case 3:
        return <NewBidFormStepThree formik={formik} />;
      default:
        return null;
    }
  };
  
  return (
    <Fragment>
      <Layout>
        <Grid container justifyContent="center" minHeight="calc(100vh - 64px)" alignItems="center" padding={3} spacing={2}>
          {(bid.loading || dashboard.loading || programs.loading || accounts.loading) ? (
            <Loader />
          ) : (
            <Grid container direction={{ lg: 'row', xs: 'column' }} spacing={2} justifyContent="center" alignItems="center" marginTop={2}>
              <Grid item xs={12} sm={6}>
                <Paper elevation={1}>
                  <form onSubmit={formik.handleSubmit}>
                    <Typography variant="h5" textAlign="center" marginBottom={2}>Nova Oferta</Typography>
                    <Alert severity="info" sx={{ m: 2 }}>
                      Caso tenha dificuldade em cadastrar sua oferta, entre em contato conosco pelo WhatsApp{" "}
                      <Link
                        href={`https://wa.me/${APP_CONTACTS.whatsapp}`}
                        target="_blank"
                        rel="noreferrer"
                        underline="hover"
                      >
                        +{APP_CONTACTS.whatsapp}
                      </Link>.
                    </Alert>
                    <Stepper activeStep={activeStep - 1} alternativeLabel>
                      {formSteps.map((label) => (
                        <Step key={label}><StepLabel>{label}</StepLabel></Step>
                      ))}
                    </Stepper>
                    <Grid container padding={2} rowSpacing={2} justifyContent="center">
                        
                      {renderStepForm(activeStep)}

                        <StepNavigation 
                          activeStep={activeStep} 
                          formSteps={formSteps} 
                          formik={formik} 
                          loading={bid.loading || account.loading || dashboard.loading || programs.loading} 
                          setActiveStep={setActiveStep}
                        />
  
                    </Grid>
                  </form>
                </Paper>
              </Grid>
              {activeStep !== 1 && (
                <Grid item xs={0} sm={4}>
                  <Paper elevation={1} style={{padding: 16}}>
                    <Typography variant="h5" textAlign="center" marginBottom={2}>Ranking</Typography>
                    <FormHelperText >Quanto menor o preço por 1.000 milhas, maior a prioridade no ranking e a chance de venda rápida.</FormHelperText>
                    {isPriceAboveRanking && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        O valor da sua oferta está alto em relação ao ranking. Para vender mais rápido, considere reduzir o preço.
                      </Alert>
                    )}
                    <Grid container padding={2} rowSpacing={2} justifyContent="center" style={formik.values.company && formik.values.price ? undefined : {
                      filter: 'blur(4px)'
                    }}>
                        <Ranking 
                          ranking={ranking?.ranking ?? Array.from({length: 4}).fill({user: 'Lor** Ip***', price_per_k: 0})}
                          bid={{user: me?.name || '', price_per_k: formik.values.price}}
                        />
                    </Grid>
                  </Paper>
                </Grid>
              )}
              <Grid item xs={12} textAlign={"center"}>
              Ficou com dúvida? Confira nosso FAQ <Link href="/FAQ-ofertas.pdf" target="_blank">clicando aqui</Link>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Layout>

      <CreateAccountModal
        accountLoading={account.loading}
        formik={createAccountFormik}
        isModalOpen={createAccountModalVisibility} 
        modalVisibilityControls={createAccountModalVisibilityControls}
      />
    </Fragment>
  );
};

export default NewBid;
